import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import Session, { IS_SELLSIDE } from '../../services/Session';
import { loanBuySideAdjustmentsSelector } from '../../../view/components/PricingGrid/selectors';
import { useIsZeroBasedPricing } from './useIsZeroBasedPricing';

const FLIPPED_TYPES = new Set<string>([
  'CustomPriceAdjustment',
  'ReLockFeeAdjustment',
  'CorporatePriceConcession',
  'BranchPriceConcession',
]);

const isNumericPrice = (price) =>
  price != null &&
  String(price).trim() !== '' &&
  Number.isFinite(Number(price));

const flipRow = (row) =>
  isNumericPrice(row.price)
    ? { ...row, price: (-Number(row.price)).toFixed(3) }
    : row;

export const flipToHundredBase = (variations): Record<string, any[]> =>
  Object.fromEntries(
    (Object.entries(variations ?? {}) as [string, any[]][]).map(
      ([type, rows]) => [
        type,
        FLIPPED_TYPES.has(type) ? rows.map(flipRow) : rows,
      ],
    ),
  );

// Buy side adjustments with manual rows sign-flipped for a zero base sell side.
// FLIPPED_TYPES must match SellSideAdjustmentTypes.FlippedForZeroBase in partner-api.
export const useDisplayBuySideAdjustments = () => {
  const variations = useSelector(loanBuySideAdjustmentsSelector());
  const isZeroBasedPricing = useIsZeroBasedPricing();
  const isSellSide = Session.get(IS_SELLSIDE);
  return useMemo(
    () =>
      isSellSide && isZeroBasedPricing
        ? flipToHundredBase(variations)
        : variations,
    [variations, isZeroBasedPricing, isSellSide],
  );
};
